import { MagicSearchResponseDTO, MagicSearchResultDTO } from '@iota/inh-core-v1-management-client';
import { buildLink } from './magic-search-link.builder';
import {
    ACTIVE_UNTIL_LABEL,
    CATEGORY_LABEL,
    CATEGORY_ORDER,
    DEFAULT_ACTIVE_UNTIL_LABEL,
    ENTITY_ICON,
    LABEL_SEPARATOR,
    MagicSearchActiveUntil,
    MagicSearchGroup,
    MagicSearchRow
} from '../models/magic-search.model';
import { formatDate } from '../../shared/utils';

/** A product's number is internal, and an entity's number and label are the same for number-like entities. */
function buildLabel(dto: MagicSearchResultDTO): string {
    const parts = dto.entityType === 'PRODUCT' ? [dto.label] : [dto.number, dto.label];
    const present = parts.filter((part): part is string => !!part?.trim());
    return [...new Set(present)].join(LABEL_SEPARATOR);
}

/** The date drives the badge; entity types without their own term get the generic label. */
function buildActiveUntil(dto: MagicSearchResultDTO): MagicSearchActiveUntil | undefined {
    const date = formatDate(dto.activeUntil);
    if (!date) {
        return undefined;
    }

    const label = (dto.entityType ? ACTIVE_UNTIL_LABEL[dto.entityType] : undefined) ?? DEFAULT_ACTIVE_UNTIL_LABEL;
    return { label, date };
}

export function toRow(dto: MagicSearchResultDTO): MagicSearchRow {
    const entityType = dto.entityType;
    return {
        key: `${entityType}-${dto.entityId}-${dto.languageCode ?? ''}`,
        icon: entityType ? ENTITY_ICON[entityType] : '',
        label: buildLabel(dto),
        additionalData: dto.additionalData,
        badge: entityType === 'PRODUCT' ? dto.languageCode : undefined,
        activeUntil: buildActiveUntil(dto),
        link: buildLink(dto)
    };
}

export function toGroups(response?: MagicSearchResponseDTO): MagicSearchGroup[] {
    if (!response) {
        return [];
    }

    const groups: MagicSearchGroup[] = [];

    const perfectMatches = response.perfectMatches ?? [];
    if (perfectMatches.length > 0) {
        groups.push({
            type: 'PERFECT',
            labelId: CATEGORY_LABEL.PERFECT,
            rows: perfectMatches.map(toRow)
        });
    }

    const results = response.results ?? [];
    for (const category of CATEGORY_ORDER) {
        const rows = results.filter((result) => !!result.entityType && category.entityTypes.includes(result.entityType)).map(toRow);
        if (rows.length > 0) {
            groups.push({
                type: category.type,
                labelId: CATEGORY_LABEL[category.type],
                rows
            });
        }
    }

    return groups;
}
